import {useTranslation} from 'react-i18next';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarProvider,
  Skeleton,
  cn,
} from '@chekinapp/ui';
import {PAGE_COLUMN, PAGE_TOP_INSET} from 'components/PageLayout/pageColumn';

const NAV_GROUPS = [[64], [88, 72, 96], [68, 84, 76, 80], [72]];

function SideBarSkeleton() {
  return (
    <SidebarProvider className="min-h-0 w-full">
      <Sidebar
        collapsible="none"
        className="page-sidebar sticky top-[var(--maintenance-banner-height,0px)] h-[var(--app-vh,100vh)] w-full overflow-hidden"
      >
        <SidebarHeader className="gap-0 p-0">
          <div className="flex items-center gap-[11px] px-[22px] pb-[18px] pt-[22px]">
            <Skeleton onScene className="size-[38px] shrink-0 rounded-[11px]" />
            <div className="min-w-0 flex-1">
              <Skeleton onScene className="h-[13px] w-[124px] rounded" />
              <Skeleton onScene className="mt-2 h-[9px] w-[72px] rounded" />
            </div>
          </div>
        </SidebarHeader>

        <SidebarContent className="pb-5">
          {NAV_GROUPS.map((labelWidths, groupIndex) => (
            <SidebarGroup key={groupIndex}>
              {groupIndex > 0 && (
                <Skeleton onScene className="mb-2 ml-2 h-[9px] w-[54px] rounded" />
              )}
              {labelWidths.map((labelWidth, itemIndex) => (
                <div key={itemIndex} className="flex items-center gap-2.5 px-2 py-[9px]">
                  <Skeleton onScene className="size-[18px] shrink-0 rounded-md" />
                  <Skeleton
                    onScene
                    className="h-[11px] rounded"
                    style={{width: labelWidth}}
                  />
                </div>
              ))}
            </SidebarGroup>
          ))}
        </SidebarContent>

        <SidebarFooter className="mx-[14px] flex-row items-center gap-2.5 border-t border-[var(--sidebar-border)] px-2 pb-4 pt-3.5">
          <Skeleton onScene className="size-8 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1">
            <Skeleton onScene className="h-[11px] w-[52px] rounded" />
            <Skeleton onScene className="mt-2 h-[9px] w-[108px] rounded" />
          </div>
        </SidebarFooter>
      </Sidebar>
    </SidebarProvider>
  );
}

function PageContentSkeleton() {
  const {t} = useTranslation();

  return (
    <div
      data-app-loader=""
      role="status"
      aria-label={t('loading')}
      className="flex min-h-[var(--app-dvh,100dvh)] w-full flex-col overflow-x-clip lg:min-h-0"
    >
      <div className={cn('mt-[14px] px-4 lg:mt-0 lg:px-8', PAGE_TOP_INSET)}>
        <Skeleton className="h-8 w-[136px] rounded-full" />
      </div>

      <div
        className={cn(
          'mx-auto mt-3 flex w-full max-w-[640px] flex-1 flex-col px-4 lg:flex-none',
          PAGE_COLUMN,
        )}
      >
        <div className="mb-5">
          <Skeleton className="h-[11px] w-[84px] rounded" />
          <Skeleton className="mt-3 h-[26px] w-[66%] rounded-lg" />
          <Skeleton className="mt-3.5 h-[13px] w-full rounded" />
          <Skeleton className="mt-2 h-[13px] w-[52%] rounded" />
        </div>

        <div className="flex flex-col gap-3">
          <Skeleton className="h-[132px] w-full rounded-[var(--chekin-radius-xl)]" />
          <Skeleton className="h-[92px] w-full rounded-[var(--chekin-radius-lg)]" />
          <Skeleton className="h-[92px] w-full rounded-[var(--chekin-radius-lg)]" />
        </div>
      </div>
    </div>
  );
}

export {PageContentSkeleton, SideBarSkeleton};
